import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router';
import { downloadApiFile } from '../../api/download';
import { getOperation } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import { isOperationSettled } from '../../api/types';
import { CopyableText } from '../../components/CopyableText';
import { FlowisAvailabilityBanner } from '../../components/FlowisAvailabilityBanner';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { GateStatusChip, OperationStatusChip } from '../../components/StatusChips';
import { useHasFullAccess } from '../../auth/useCallerContext';
import { useErrorToast } from '../../hooks/useErrorToast';
import { useNoTenantMessageKey, useSelectedTenant } from '../../tenant/tenantSelection';
import { formatDateTime } from '../../utils/format';

/** While an operation is queued the relay has not run yet, so the detail keeps asking. */
const queuedPollIntervalMs = 3000;

/**
 * Delivered, but Flowis has not finished with it. The server's poller is what follows the document
 * from there; this only has to notice when the recorded status moves.
 */
const openPollIntervalMs = 30000;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 180 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}
    </Box>
  );
}

export function OperationDetailPage() {
  const { t } = useTranslation();
  const { operationGid = '' } = useParams();
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const { selectedTenantGid } = useSelectedTenant();
  // Nothing to query: an administrator has not picked a tenant yet, or the caller holds none.
  const hasNoTenant = selectedTenantGid === null;
  const noTenantKey = useNoTenantMessageKey();
  // A bare registration submitter lands here after handing a document in. The operations list and
  // the file downloads are beyond their role, so those controls are not offered to them.
  const hasFullAccess = useHasFullAccess();
  const [refreshing, setRefreshing] = useState(false);

  // A tenant-less query reaches the fallback schema and fails instead of returning an empty result.
  const query = useQuery({
    queryKey: queryKeys.operations.detail(selectedTenantGid, operationGid),
    queryFn: ({ signal }) => getOperation(operationGid, false, signal),
    enabled: operationGid.length > 0 && selectedTenantGid !== null,
    refetchInterval: (q) => {
      const current = q.state.data;
      if (!current) return false;
      if (current.status === 'Queued') return queuedPollIntervalMs;

      return isOperationSettled(current) ? false : openPollIntervalMs;
    },
  });

  const operation = query.data;

  const refreshFromFlowis = async () => {
    setRefreshing(true);
    try {
      const refreshed = await getOperation(operationGid, true);
      queryClient.setQueryData(queryKeys.operations.detail(selectedTenantGid, operationGid), refreshed);
      await queryClient.invalidateQueries({ queryKey: queryKeys.operations.all });
    } catch (error) {
      toastError(error);
    } finally {
      setRefreshing(false);
    }
  };

  const download = async (fileGid: string, fallbackFileName: string) => {
    try {
      await downloadApiFile(`/file/${fileGid}`, { fallbackFileName });
    } catch (error) {
      toastError(error);
    }
  };

  if (hasNoTenant) return <EmptyBlock message={t(noTenantKey)} />;
  if (query.isPending) return <LoadingBlock label={t('operations.detail.loading')} />;
  if (query.isError) return <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />;
  if (!operation) return null;

  const queued = operation.status === 'Queued';

  // Still queued but already carrying an error: the relay has tried, could not get through, and is
  // going to try again.
  const retrying = queued && operation.errors.length > 0;

  return (
    <Stack spacing={2}>
      <FlowisAvailabilityBanner />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          component={RouterLink}
          to={hasFullAccess ? '/operations' : '/submit'}
          startIcon={<ArrowBackIcon />}
        >
          {t(hasFullAccess ? 'nav.operations' : 'nav.submit')}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {/* The log holds what the operation cannot: the message that created it, the acknowledgement
            sent back, every status change announced — and the communication log is a full-access section. */}
        {hasFullAccess && (
          <Button
            component={RouterLink}
            to={`/communication?search=${encodeURIComponent(operation.externalDocumentId ?? operation.operationGid)}`}
            startIcon={<ManageSearchIcon />}
            variant="outlined"
          >
            {t('operations.detail.communicationLog')}
          </Button>
        )}
        {/* Nothing to ask Flowis about until it has issued a document id. */}
        <Tooltip
          title={
            operation.flowisDocumentId
              ? t('operations.detail.refreshTooltip')
              : t('operations.detail.refreshNoIdTooltip')
          }
        >
          <span>
            <Button
              startIcon={<RefreshIcon />}
              variant="outlined"
              disabled={!operation.flowisDocumentId || refreshing}
              onClick={() => void refreshFromFlowis()}
            >
              {t('operations.detail.refresh')}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <OperationStatusChip status={operation.status} retrying={retrying} />
            {operation.duplicate && (
              <Chip size="small" color="warning" label={t('operations.detail.duplicateChip')} />
            )}
            {operation.flowisStatus && <GateStatusChip status={operation.flowisStatus} />}
          </Stack>

          {queued && (
            <Box sx={{ mb: 2 }}>
              {retrying ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {t('operations.detail.retryingAlert')}
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {t('operations.detail.queuedAlert')}
                </Alert>
              )}
              <LinearProgress />
            </Box>
          )}

          <Stack direction="row" spacing={4} useFlexGap flexWrap="wrap">
            <Field label={t('operations.detail.operation')}>
              <CopyableText value={operation.operationGid} />
            </Field>
            <Field label={t('operations.detail.account')}>
              {/* The address it went out under, which is also how a sender named it. */}
              {operation.participantId ? (
                <CopyableText value={operation.participantId} />
              ) : (
                <CopyableText value={operation.accountGid} />
              )}
            </Field>
            {operation.externalDocumentId && (
              <Field label={t('operations.detail.externalDocumentId')}>
                <CopyableText value={operation.externalDocumentId} />
              </Field>
            )}
            <Field label={t('operations.detail.flowisDocumentId')}>
              {operation.flowisDocumentId ? <CopyableText value={operation.flowisDocumentId} /> : '—'}
            </Field>
            <Field label={t('operations.detail.invoiceNumber')}>{operation.flowisInvoiceId ?? '—'}</Field>
            <Field label={t('operations.detail.submitted')}>{formatDateTime(operation.submittedAt)}</Field>
            <Field label={t('operations.detail.lastChecked')}>
              {formatDateTime(operation.lastCheckedAt)}
            </Field>
          </Stack>
        </CardContent>
      </Card>

      {/* Downloads go through /file, which a bare registration submitter's role does not reach. */}
      {hasFullAccess && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {t('operations.detail.files')}
            </Typography>
            <Stack spacing={1} alignItems="flex-start">
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => void download(operation.documentFileGid, 'document.xml')}
              >
                {t('operations.detail.document')}
              </Button>
              {operation.sentPayloadFileGid && (
                <Tooltip title={t('operations.detail.transmittedPayloadTooltip')}>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => void download(operation.sentPayloadFileGid ?? '', 'sent-payload.xml')}
                  >
                    {t('operations.detail.transmittedPayload')}
                  </Button>
                </Tooltip>
              )}
              {operation.attachments.map((attachment) => (
                <Button
                  key={attachment.fileGid}
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => void download(attachment.fileGid, attachment.fileName)}
                >
                  {attachment.fileName}
                </Button>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {operation.errors.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {retrying ? t('operations.detail.retryingTitle') : t('operations.detail.errorsTitle')}
            </Typography>
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('operations.detail.errorCode')}</TableCell>
                    <TableCell>{t('operations.detail.errorDetail')}</TableCell>
                    <TableCell>{t('operations.detail.errorField')}</TableCell>
                    <TableCell align="right">{t('operations.detail.errorLine')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operation.errors.map((error, index) => (
                    <TableRow key={`${error.code}-${index}`}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{error.code}</TableCell>
                      <TableCell>{error.detail}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{error.attr ?? '—'}</TableCell>
                      <TableCell align="right">{error.line ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
